import React, { useMemo } from "react";
import { Grid } from "semantic-ui-react";
import { AccordionTitleView, DropDown, InputText, Switch } from "../../../components";
import { DATA_TYPES, FARMING_TYPES, FIELD_TYPES, INGREDIENT_TYPE } from "../../../config/constants";

import { useGetCountries } from "../../../api/static-data";

interface EnvData {
  ingredientType: string;
  dataType: string;
  country: number;
  farmingType: string;
  fieldType: string;
  produceType: string;
  produceCultivar: string;
  specifyIngredientValue: string;
}

interface IngredientEnvDetailsProps {
  originalEnvData?: EnvData | null;
  simulatedEnvData?: EnvData | null;
  setSimulatedEnvData?: (data: EnvData) => void;
  isEnvDataAvailable?: boolean;
  setIsEnvDataAvailable?: (available: boolean) => void;
  register: any;
  control: any;
  errors: Record<string, any>;
}

const IngredientEnvDetails: React.FC<IngredientEnvDetailsProps> = ({
  originalEnvData,
  simulatedEnvData,
  setSimulatedEnvData,
  isEnvDataAvailable,
  setIsEnvDataAvailable,
  register,
  control,
  errors
}) => {
  const produceIngredient = true;
  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const getCountries = useMemo(() => {
    if (countriesData && countriesData.length > 0) {
      return countriesData.map((country: any) => ({
        key: country.id,
        text: country.country,
        value: country.value
      }));
    }
    return [];
  }, [isCountriesDataSuccess]);

  const isProcessedIngredient = useMemo(() => {
    return simulatedEnvData?.ingredientType === "PROCESSED";
  }, [simulatedEnvData]);

  return (
    <>
      <AccordionTitleView accordionEnabled={false} title={"Environmental Data"} plusIcon={false} />
      <Grid style={{ marginTop: isEnvDataAvailable ? "-25px" : "25px" }}>
        <Grid.Row divided>
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Grid columns={8}>
              <Grid.Row columns={"equal"}>
                <Grid.Column>
                  <p className="textLabel mb-0">Ingredient Type</p>
                  <p className="textLabel">{originalEnvData?.ingredientType}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Data Type</p>
                  <p className="textLabel">{originalEnvData?.dataType}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Produce Type</p>
                  <p className="textLabel">{originalEnvData?.produceType}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Produce Cultivar</p>
                  <p className="textLabel">{originalEnvData?.specifyIngredientValue}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Farming Type</p>
                  <p className="textLabel">{originalEnvData?.farmingType}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Field Type</p>
                  <p className="textLabel">{originalEnvData?.fieldType}</p>
                </Grid.Column>
                <Grid.Column>
                  <p className="textLabel mb-0">Country</p>
                  <p className="textLabel">{originalEnvData?.country}</p>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Grid.Column computer={16} tablet={16} mobile={16}>
              <div className="enviorenmentDataSelectBox">
                <Grid>
                  <Grid.Column computer={8} tablet={8} mobile={8}>
                    <p className="enviorenmentDataSelectTitle">
                      Do you want to add environmental data?
                    </p>
                  </Grid.Column>
                  <Grid.Column computer={8} tablet={8} mobile={8}>
                    <Switch
                      onClick={() => {
                        setIsEnvDataAvailable?.(!isEnvDataAvailable);
                      }}
                      titleOne={"No"}
                      titleTwo={"Yes"}
                      titleOneValue={"No"}
                      titleTwoValue={"Yes"}
                      defaultValue={isEnvDataAvailable ? "Yes" : "No"}
                    />
                  </Grid.Column>

                  {isEnvDataAvailable && (
                    <Grid.Column
                      computer={16}
                      tablet={16}
                      mobile={16}
                      className="paddingRemoveBottom"
                    >
                      <Grid>
                        <Grid.Column
                          computer={8}
                          tablet={8}
                          mobile={16}
                          className="paddingRemoveTop"
                        >
                          <DropDown
                            labelName="Ingredient Type"
                            placeholder="Ingredient Type"
                            defaultValue={simulatedEnvData?.ingredientType || ""}
                            currentData={INGREDIENT_TYPE}
                            handleChangeState={(e: any, { value }: any) => {
                              setSimulatedEnvData?.({
                                ...simulatedEnvData,
                                ingredientType: value
                              } as EnvData);
                            }}
                            search
                            key={"ingredient_type"}
                            customGridColumn={"customGridColomnTyp"}
                          />
                        </Grid.Column>
                        <Grid.Column
                          computer={8}
                          tablet={8}
                          mobile={16}
                          className="paddingRemoveTop"
                        >
                          <DropDown
                            labelName="Data Type"
                            placeholder="Select Type"
                            defaultValue={simulatedEnvData?.dataType || ""}
                            currentData={[...DATA_TYPES]}
                            customGridColumn={"customGridColomnTyp"}
                            handleChangeState={(e: any, { value }: any) => {
                              setSimulatedEnvData?.({
                                ...simulatedEnvData,
                                dataType: value
                              } as EnvData);
                            }}
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Column>
                  )}
                </Grid>
              </div>
            </Grid.Column>
            {isEnvDataAvailable && (
              <Grid.Column computer={16} tablet={16} mobile={16}>
                <Grid style={{ marginTop: 10 }}>
                  {produceIngredient && (
                    <>
                      <Grid.Column computer={4} tablet={8} mobile={16} className="paddingRemoveTop">
                        <DropDown
                          required={true}
                          labelName="Produce Type"
                          placeholder="Select Produce Type"
                          defaultValue={simulatedEnvData?.produceType || ""}
                          currentData={[]}
                          handleChangeState={(e: any, { value }: any) => {
                            setSimulatedEnvData?.({
                              ...simulatedEnvData,
                              produceType: value
                            } as EnvData);
                          }}
                          customGridColumn={"customGridColomnTyp"}
                          search
                          key={"produceType"}
                          errorMessage={"Produce is required"}
                          customRequired={true}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16} className="paddingRemoveTop">
                        <InputText
                          register={register}
                          errors={errors?.produceCultivar}
                          labelName={"Produce Cultivar"}
                          placeholder="Produce Cultivar"
                          name="produceCultivar"
                          required={false}
                          customGridColumn={"addCultivarCustomGridColumn"}
                          defaultValue={simulatedEnvData?.produceCultivar || ""}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16} className="paddingRemoveTop">
                        <DropDown
                          labelName="Farming Type"
                          placeholder="Select Farming Type"
                          defaultValue={simulatedEnvData?.farmingType || ""}
                          currentData={FARMING_TYPES}
                          customGridColumn={"customGridColomnTyp"}
                          handleChangeState={(e: any, { value }: any) => {
                            setSimulatedEnvData?.({
                              ...simulatedEnvData,
                              farmingType: value
                            } as EnvData);
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16} className="paddingRemoveTop">
                        <DropDown
                          labelName="Field Type"
                          placeholder="Select Field Type"
                          defaultValue={simulatedEnvData?.fieldType || ""}
                          currentData={FIELD_TYPES}
                          customGridColumn={"customGridColomnTyp"}
                          handleChangeState={(e: any, { value }: any) => {
                            setSimulatedEnvData?.({
                              ...simulatedEnvData,
                              fieldType: value
                            } as EnvData);
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16} className="paddingRemoveTop">
                        <DropDown
                          labelName="Country"
                          placeholder="Select country"
                          defaultValue={
                            simulatedEnvData?.country ? simulatedEnvData.country.toString() : ""
                          }
                          currentData={getCountries}
                          handleChangeState={(e: any, { value }: any) => {
                            setSimulatedEnvData?.({
                              ...simulatedEnvData,
                              country: value
                            } as EnvData);
                          }}
                          search
                          customGridColumn={"customGridColomnTyp"}
                        />
                      </Grid.Column>
                    </>
                  )}
                  {isProcessedIngredient && (
                    <>
                      <Grid.Column computer={8} tablet={8} mobile={16} className="paddingRemoveTop">
                        <DropDown
                          required={true}
                          labelName="Specify Ingredient"
                          placeholder="Specify Ingredient"
                          defaultValue={[]}
                          currentData={[]}
                          handleChangeState={(e: any, { value }: any) => {}}
                          loading={isCountriesDataLoading}
                          search
                          key={"specify_ingredient"}
                          customGridColumn={"customGridColomnTyp"}
                          errorMessage={"Specify Ingredient is required"}
                          customRequired={true}
                        />
                      </Grid.Column>
                    </>
                  )}
                </Grid>
              </Grid.Column>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default IngredientEnvDetails;
